import React, { useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import { CallTranscriptionInfoTooltip } from '../modals/CallTranscriptionModal';
import { HighchartsReactWithStyles } from './HighchartsReactWithStyles';
import { ICallTranscriptionItem } from '../CallTranscriptions/CallInfo';
import {
  FEATURE_NOT_SUPPORTED_STYLE,
  getFeatureNotSupportedLabel,
  getLanguageCode,
  isEnglishLanguageCode,
} from '../CallTranscriptions/CallTranscriptionSummary';

import Sonification from 'highcharts/modules/sonification';
Sonification(Highcharts);

export default function SentimentAnalysis(props: {
  millisecond: number;
  totalDuration: number;
  item: ICallTranscriptionItem;
  loading: boolean;
}) {
  const { item, millisecond, loading } = props;
  const [currentPlotLine, setCurrentPlotLine] = useState(0);
  // @todo define ref type
  const chartRef = useRef<any>(null);
  const series = useMemo(() => {
    setCurrentPlotLine(item?.sentimentAnalysisResults?.[0]?.start || 0);
    const result: any = [];
    item?.sentimentAnalysisResults?.forEach((el) => {
      const sentiment =
        el?.sentiment === 'NEGATIVE' ? 0 : el?.sentiment === 'NEUTRAL' ? 1 : 2;
      result.push([el?.start, sentiment]);
      result.push([el?.end, sentiment]);
    });
    return result;
  }, [item]);

  const options = useMemo(() => {
    return {
      chart: {
        height: 200,
        type: 'line',
      },
      credits: { enabled: false },
      plotOptions: {
        series: {
          pointInterval: 1,
          pointStart: 0,
        },

        line: {
          animation: true,
        },
      },
      accessibility: {
        landmarkVerbosity: 'one',
      },
      series: [
        {
          showInLegend: false,
          data: series ?? [],
        },
      ],

      dataLables: {
        enabled: false,
      },
      title: {
        text: undefined,
      },
      tooltip: { shared: true },
      xAxis: {
        title: {
          enabled: false,
        },
        crosshair: true,
        type: 'number',
        // dateTimeLabelFormats: {
        //   day: '%H',
        // },
        plotLines: [
          {
            color: '#FF0000',
            width: 2,
            value: item?.sentimentAnalysisResults?.[0]?.start,
          },
        ],
        labels: {
          style: {
            color: 'transparent',
          },
        },
      },
      yAxis: {
        title: {
          enabled: false,
        },
        min: 0,
        categories: ['Negative', 'Neutral', 'Positive'],
        allowDecimals: false,
        ceiling: 2,
      },
    };
  }, [item, series]);

  useEffect(() => {
    item?.sentimentAnalysisResults?.forEach((el) => {
      if (el?.start < millisecond && el?.end > millisecond) {
        if (currentPlotLine !== (el?.start + el?.end) / 2) {
          setCurrentPlotLine((el?.start + el?.end) / 2);
        }
      }
    });
  }, [millisecond, item]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart) {
      const xAxis = chart?.xAxis?.[0];
      let plotLine;
      let d;
      let newX;
      if (millisecond > currentPlotLine) {
        plotLine = xAxis?.plotLinesAndBands?.[0]?.svgElem;
        d = plotLine?.d?.split(' ');
        newX = xAxis?.toPixels(millisecond) - d?.[2];
        plotLine.animate(
          {
            translateX: newX,
          },
          millisecond - currentPlotLine
        );
      }
      // else {
      //   plotLine = xAxis?.plotLinesAndBands?.[0]?.svgElem;
      //   const plotLineLength = parseInt(d?.[2], 10);
      //   const timeDiff = currentPlotLine - millisecond;
      //   newX = xAxis?.toPixels(millisecond) - plotLineLength;
      //   plotLine.animate({
      //     translateX: newX
      //   }, timeDiff);
      // }
    }
  }, [chartRef, millisecond]);
  // useEffect(() => {
  //   const chart = chartRef?.current?.chart;
  //   if (chart) {
  //     const plotLine = chart?.xAxis[0]?.plotLinesAndBands?.[0]?.svgElem;
  //     if (plotLine) {
  //       plotLine?.stop(); // Stop any ongoing animation
  //       const xAxis = chart?.xAxis?.[0];
  //       const newX = xAxis?.toPixels(millisecond);
  //       plotLine.attr({ translateX: newX });
  //     }
  //   }
  // }, [millisecond]);

  const languageCode = getLanguageCode(item);
  const hasSentimentAnalysisFeature = isEnglishLanguageCode(languageCode);

  return (
    <div>
      <div style={{ textAlign: 'center', margin: '8px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <div style={{ fontWeight: 500, fontSize: '15px' }}>
            Sentiment Analysis
          </div>
          <div style={{ fontSize: '14px' }}>
            <CallTranscriptionInfoTooltip
              title={
                'View a sentiment trend line as it changes in real time throughout the duration of a call. Sentiment is based on language used during the call by all participants. Coach your team by pinpointing and reviewing the precise moment a call sentiment changed either up or down.'
              }
            />
          </div>
        </div>
      </div>
      {hasSentimentAnalysisFeature ? (
        <HighchartsReactWithStyles
          allowChartUpdate={true}
          highcharts={Highcharts}
          options={options}
          ref={chartRef}
          loading={loading}
        />
      ) : (
        <div style={{ ...FEATURE_NOT_SUPPORTED_STYLE, minHeight: 100 }}>
          {getFeatureNotSupportedLabel(languageCode)}
        </div>
      )}
    </div>
  );
}
